import React, { useEffect, useState } from 'react';
import { Alert, Card, Button } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import SearchForm from './SearchForm';

const SearchActivity: React.FC = () => {
  const { user } = useAuth();
  const [searches, setSearches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const fetchSearches = () => {
    if (!user) return;
    setLoading(true);
    fetch('/api/searches')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data?.searches)) {
          const mapped = data.searches.map((s: any) => ({
            id: s.id,
            query: s.search_value,
            position: s.position,
            country: s.country_code,
            timestamp: s.expiration,
          }));
          setSearches(mapped);
          setError(null);
        } else {
          throw new Error('Invalid data format');
        }
      })
      .catch(() => {
        setSearches([
          { id: '1', query: '555', position: 'prefix', country: '+1', timestamp: '2025-07-23T10:00:00Z' },
          { id: '2', query: '0123', position: 'postfix', country: '+1', timestamp: '2025-07-22T09:00:00Z' },
          { id: '3', query: '888', position: 'anywhere', country: '+1', timestamp: '2025-07-21T08:00:00Z' },
        ]);
        setError('Could not fetch live search data. Showing mock data.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSearches();
    // eslint-disable-next-line
  }, [user]);

  if (!user) return null;

  return (
    <Card className="h-100 shadow-sm w-100 dashboard-card">
      <Card.Body className="d-flex flex-column justify-content-between p-4">
        <Card.Title className="fw-bold mb-3">Your Number Searches</Card.Title>
        {showForm ? (
          <SearchForm
            onSuccess={() => {
              setShowForm(false);
              fetchSearches();
            }}
            onCancel={() => setShowForm(false)}
            disabled={searches.length >= 5}
          />
        ) : (
          <>
            <div className="mb-3">
              <Button
                variant="success"
                onClick={() => setShowForm(true)}
                disabled={searches.length >= 5}
              >
                Create New Search
              </Button>
              {searches.length >= 5 && (
                <span className="text-danger ms-2 small">Max 5 searches allowed.</span>
              )}
            </div>
            {loading ? (
              <div className="text-center text-muted py-4">Loading...</div>
            ) : error ? (
              <Alert variant="danger">{error}</Alert>
            ) : (
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {searches.length === 0 ? (
                  <div className="text-muted">No searches found.</div>
                ) : (
                  <ul className="list-unstyled mb-0">
                    {searches.map((search, idx) => (
                      <li key={idx} className="mb-2">
                        <span className="fw-semibold">{search.query}</span>
                        <span className="badge bg-secondary ms-2">{search.position}</span>
                        <span className="badge bg-info text-dark ms-2">{search.country}</span>
                        <span className="text-muted small ms-2">
                          {search.timestamp ? new Date(search.timestamp).toLocaleString() : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default SearchActivity;
